import { useMemo } from 'react'
import swal from 'sweetalert2'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCog, faPencilAlt, faPlus, faTrashAlt,
} from '@fortawesome/pro-light-svg-icons'

import handleCallbackAction from '@functions/handleCallbackAction'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import {
  Box,
  Button,
  Checkbox,
  DashboardModule,
  IconWithMenu,
  Link,
  StatusBadge,
  Table,
  Tag,
  Text,
} from '@domain-group/suit-theme'
import { digObject } from '@domain-group/javascript-utils'
import { useWatchEntityUpdates } from '@domain-group/react-hooks'

import type { EntitiesState } from '@redux/entities'

import { TaskListItemModel } from '@models/taskListItem'
import { TaskListSectionModel } from '@models/taskListSection'

import useSelector from '@hooks/useSelector'
import useTaskListItem, { generateUrls as generateTaskListItemUrls } from '@hooks/useTaskListItem'
import useTaskListItems from '@hooks/useTaskListItems'
import useTaskListSection from '@hooks/useTaskListSection'

import { getRelatedEntityOwnerOrSubject } from '@functions/relatedEntity'
import { getColor } from '@functions/status'

import DateBadge from '@components/DateBadge'

const watchEntityKeys = ['statuses', 'taskListItems']

const confirmDeleteTaskListSection = (deleteFn: () =>void) => {
  const deleteTaskListSectionPayload: HandleCallbackActionParams = {
    callbacks: {
      // @ts-expect-error TS2322
      action: deleteFn,
    },
    toastText: 'Task List Section Deleted',
  }

  swal
    .fire({
      title: 'Delete Task List Section?',
      text: 'Are you sure?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it.',
      confirmButtonColor: '#DD6B55',
    })
    .then(({ value }) => {
      if (value){
        handleCallbackAction(deleteTaskListSectionPayload)
      }
    })
}

const buildColumns = (entities: EntitiesState) => {
  const { statuses } = entities

  return [
    {
      title: '',
      dataKey: 'completed',
      cellProps: {
        verticalAlign: 'middle',
        width: 18,
      },
      render: (completed: boolean) => (
        <Checkbox boxProps={{ width: 18 }} checked={completed} marginRight="0" />
      ),
    },
    {
      title: 'Title',
      dataKey: 'title',
      cellProps: {
        verticalAlign: 'middle',
      },
      render: (title: string, item: TaskListItemModel) => {
        const { editTaskListItemUrl } = generateTaskListItemUrls(item, { includeRedirect: true })

        return (
          <Link
            href={editTaskListItemUrl}
            textProps={{
              color: 'bodyFontColor', display: 'inline-block', variant: 'ellipsis', width: 320,
            }}
          >
            {title}
          </Link>
        )
      },
    },
    {
      title: 'Product',
      dataKey: 'product_title',
      cellProps: {
        verticalAlign: 'middle',
      },
      // @ts-expect-error TS7006
      render: (_, item: TaskListItemModel) => {
        const product = getRelatedEntityOwnerOrSubject({
          // @ts-expect-error TS2322
          entities, item, returnType: 'subject', subjectType: 'ServiceProduct',
        })

        return (
          <Text
            color={product?.title ? 'bodyFontColor' : 'lightGrey'}
            fontStyle={product?.title ? undefined : 'italic'}
            style={{ whiteSpace: 'nowrap' }}
          >
            {product?.title || 'Not Set'}
          </Text>
        )
      },
    },
    {
      title: 'Provider',
      dataKey: 'provider_title',
      cellProps: {
        verticalAlign: 'middle',
      },
      // @ts-expect-error TS7006
      render: (_, item: TaskListItemModel) => {
        const provider = getRelatedEntityOwnerOrSubject({
          // @ts-expect-error TS2322
          entities, item, returnType: 'subject', subjectType: 'Provider',
        })

        return (
          <Text
            color={provider?.title ? 'bodyFontColor' : 'lightGrey'}
            fontStyle={provider?.title ? undefined : 'italic'}
            style={{ whiteSpace: 'nowrap' }}
          >
            {provider?.title || 'Not Set'}
          </Text>
        )
      },
    },
    {
      title: 'Template #',
      dataKey: 'template_page_id',
      cellProps: {
        textAlign: 'center',
        verticalAlign: 'middle',
      },
      // @ts-expect-error TS7006
      render: (_, item: TaskListItemModel) => {
        const templatePage = getRelatedEntityOwnerOrSubject({
          entities,
          filters: { 'data.template_type': 'new' },
          // @ts-expect-error TS2322
          item,
          returnType: 'subject',
          subjectType: 'TemplatePage',
        })

        return (
          <Link
            textProps={{
              color: templatePage?.id ? 'bodyFontColor' : 'lightGrey',
              fontStyle: templatePage?.id ? undefined : 'italic',
            }}
          >
            {templatePage?.id || 'Not Set'}
          </Link>
        )
      },
    },
    {
      title: 'Due Date',
      cellProps: {
        key: 'due_date',
        textAlign: 'center',
        verticalAlign: 'middle',
      },
      dataKey: 'due_date',
      render: (_: string, taskListItem: TaskListItemModel) => {
        const dueDate = digObject(taskListItem, 'dates.due.date')

        if (!dueDate) return (<Text fontStyle="italic">Not Set</Text>)

        return (
          <DateBadge date={dueDate} />
        )
      },
    },
    {
      title: 'Priority',
      cellProps: {
        textAlign: 'center',
        verticalAlign: 'middle',
      },
      dataKey: 'priority',
      // @ts-expect-error TS2552
      render: (priority: PriorityKey) => {
        const priorityKey = priority || 'default'
        return <StatusBadge color={getColor('priority', priorityKey)} ghost text={priorityKey} />
      },
    },
    {
      title: 'Status',
      dataKey: 'status_id',
      cellProps: {
        textAlign: 'center',
        verticalAlign: 'middle',
      },
      render: (statusId: number) => {
        const status = statuses[statusId] || {}
        return <StatusBadge color={getColor('status', status.key)} ghost text={status.title || 'Pending'} />
      },
    },
  ]
}

interface TaskListSectionProps {
  taskListSection: Partial<TaskListSectionModel>,
}

const TaskListSection = (props: TaskListSectionProps) => {
  const { taskListSection } = props

  const taskListSectionPayload = useTaskListSection(taskListSection)
  const {
    callbacks: {
      createOrEditTaskListSection,
      deleteTaskListSection: deleteFn,
    },
    taskListSection: {
      dates,
      id,
      task_list_id,
      title,
    },
  } = taskListSectionPayload

  // @ts-expect-error TS2345
  const dueDate = digObject(dates, 'due.date')

  const taskListItemPayload = useTaskListItem({ task_list_id, task_list_section_id: id })
  const {
    callbacks: {
      createOrEditTaskListItem,
    },
  } = taskListItemPayload

  const taskListItemsPayload = useTaskListItems({
    // @ts-expect-error TS2741
    filters: {
      parent_task_id: 'none',
      task_list_id,
      task_list_section_id: id,
    },
    performHttpRequests: !!id,
    requestOptions: {
      include_related_entity_subject: true,
      include_task_list_item_related_entities: true,
    },
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filteredTaskListItems,
    filteredTaskListItemsCount,
    loading,
  } = taskListItemsPayload

  const entities = useSelector(reduxState => reduxState.entities)

  const {
    updatedEntities: {
      statuses: statusesUpdatedAt,
      taskListItems: taskListItemsUpdatedAt,
    },
  } = useWatchEntityUpdates(watchEntityKeys)

  const memoColumns = useMemo(
    () => buildColumns(entities),
    [filteredTaskListItemsCount, statusesUpdatedAt, taskListItemsUpdatedAt],
  )

  return (
    <DashboardModule
      boxProps={{ id: `taskListSection-${id}` }}
      headerRight={(
        <>
          {!!dueDate && (
            <Tag
              boxProps={{
                color: 'bodyFontColor',
                display: 'inline-flex',
                fontSize: 'xxsmall',
                fontStyle: 'normal',
                marginRight: 0,
                paddingX: 'medium',
                variant: 'whiteGrey',
              }}
              // @ts-expect-error TS2322
              width="auto"
            >
              {dueDate}
            </Tag>
          )}

          <IconWithMenu
            boxProps={{ marginLeft: 'medium' }}
            icon={(
              <Link greyLink>
                <FontAwesomeIcon icon={faCog} />
              </Link>
            )}
          >
            {/*
             // @ts-expect-error TS2741 */}
            <IconWithMenu.Menu closeOnClick listStyle={{ right: '-22px' }}>
              <Link key="edit" onClick={() => createOrEditTaskListSection()}>
                <FontAwesomeIcon icon={faPencilAlt} /> Edit Group
              </Link>

              {!filteredTaskListItemsCount && (
                // @ts-expect-error TS2345
                <Link key="edit" onClick={() => confirmDeleteTaskListSection(deleteFn)}>
                  <FontAwesomeIcon icon={faTrashAlt} /> Delete Section
                </Link>
              )}
            </IconWithMenu.Menu>
          </IconWithMenu>
        </>
      )}
      loading={loading}
      title={title}
    >
      <Box flexDirection="column">
        <Table
          boxProps={{ border: 'none', overflowY: 'auto' }}
          // @ts-expect-error TS2322
          columns={memoColumns}
          data={filteredTaskListItems}
          stickyHeader
        />

        <DashboardModule.LoadMoreFooter
          boxProps={{ borderTop: loading ? 'none' : '1px solid' }}
          callbacks={{ loadMore }}
          canLoadMore={canLoadMore}
          entityCount={filteredTaskListItemsCount}
          loading={loading}
        >
          <Button
            buttonStyle="secondaryEdit"
            icon={<FontAwesomeIcon icon={faPlus} />}
            onClick={() => createOrEditTaskListItem()}
            size="small"
            // @ts-expect-error TS2322
            width={['100%', 'fit-content']}
          >
            Add Task to Group
          </Button>
        </DashboardModule.LoadMoreFooter>
      </Box>
    </DashboardModule>
  )
}

export default TaskListSection
